import { useEffect, useState } from 'react'

/**
 * 3x3 grid regions for sampling specific areas of an image.
 *
 *   TL | TC | TR
 *   ---+----+---
 *   ML | MC | MR
 *   ---+----+---
 *   BL | BC | BR
 */
export type GridRegion = 'TL' | 'TC' | 'TR' | 'ML' | 'MC' | 'MR' | 'BL' | 'BC' | 'BR'

const GRID_OFFSETS: Record<GridRegion, { col: number; row: number }> = {
  TL: { col: 0, row: 0 },
  TC: { col: 1, row: 0 },
  TR: { col: 2, row: 0 },
  ML: { col: 0, row: 1 },
  MC: { col: 1, row: 1 },
  MR: { col: 2, row: 1 },
  BL: { col: 0, row: 2 },
  BC: { col: 1, row: 2 },
  BR: { col: 2, row: 2 },
}

export interface UseImageLuminanceOptions {
  bgLuminance?: number
  opacity?: number
  /** Which grid cells to sample. Defaults to all 9 (full image). */
  regions?: GridRegion[]
}

const computeLuminance = (data: Uint8ClampedArray): number => {
  let total = 0
  const pixelCount = data.length / 4
  for (let i = 0; i < data.length; i += 4) {
    total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
  }
  return total / pixelCount
}

/**
 * Samples an image at low resolution and returns whether it appears dark.
 * When bgLuminance and opacity are provided, the result accounts for the
 * image being composited at that opacity over the background — matching
 * what the user actually sees (e.g. a blurred image at 90% over a light bg).
 *
 * Use `regions` to sample specific areas of the image (3x3 grid).
 * Returns `null` while loading, `true` for dark images, `false` for light.
 */
export const useImageLuminance = (
  src?: string | null,
  { bgLuminance = 0, opacity = 1, regions }: UseImageLuminanceOptions = {},
): boolean | null => {
  const [isDark, setIsDark] = useState<boolean | null>(null)
  const regionsKey = regions && regions.length > 0 ? [...regions].sort().join(',') : 'all'

  useEffect(() => {
    if (!src) {
      setIsDark(null)
      return
    }

    const selectedRegions = regionsKey === 'all' ? null : (regionsKey.split(',') as GridRegion[])

    let cancelled = false
    const img = new window.Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      if (cancelled) return
      try {
        const canvas = document.createElement('canvas')
        const size = 33 // divisible by 3 for clean grid cells
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        ctx.drawImage(img, 0, 0, size, size)

        let avgLuminance: number
        if (!selectedRegions) {
          const { data } = ctx.getImageData(0, 0, size, size)
          avgLuminance = computeLuminance(data)
        } else {
          const cellSize = size / 3
          let totalLuminance = 0
          for (const region of selectedRegions) {
            const { col, row } = GRID_OFFSETS[region]
            const { data } = ctx.getImageData(col * cellSize, row * cellSize, cellSize, cellSize)
            totalLuminance += computeLuminance(data)
          }
          avgLuminance = totalLuminance / selectedRegions.length
        }

        const perceived = avgLuminance * opacity + bgLuminance * (1 - opacity)
        setIsDark(perceived < 128)
      } catch {
        // CORS tainted canvas — can't read pixels, assume dark (image is over dark bg)
        setIsDark(true)
      }
    }
    img.onerror = () => {
      if (!cancelled) setIsDark(null)
    }
    img.src = src

    return () => {
      cancelled = true
    }
  }, [src, bgLuminance, opacity, regionsKey])

  return isDark
}
